import { motion } from "framer-motion";
import { TrendingUp, Clock3, BarChart3, Gauge, Lock } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const SPARK = "M0,52 C20,50 30,38 45,40 C60,42 70,24 85,26 C100,28 110,34 125,22 C140,10 155,16 170,12 C185,8 200,14 220,6";
const BARS = [38, 55, 48, 72, 90, 64, 30];
const DAYS = ["S", "T", "Q", "Q", "S", "S", "D"];

export default function Insights() {
  return (
    <section id="insights" data-testid="insights-section" className="relative overflow-hidden py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <ChapterHeading
              index="08"
              eyebrow="CliniAtiva Insights"
              lines={["Sua operação,", "em números vivos."]}
              sub="Dashboard exclusivo com taxa de conversão, agendamentos por horário, picos de movimento e retorno em tempo real. Acesso garantido para os 40 primeiros clientes."
            />
            <Reveal delay={0.25} className="mt-10">
              <div className="flex items-center gap-4">
                <button
                  data-testid="insights-cta-demo"
                  onClick={() => scrollToId("demonstracao")}
                  className="rounded-full bg-petrol px-7 py-4 font-display text-sm font-bold text-white transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_12px_30px_rgba(18,60,74,0.35)] dark:bg-turquoise dark:text-petrol"
                >
                  Garantir meu acesso antecipado
                </button>
                <span className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-widest text-[var(--muted)]">
                  <Lock className="h-3.5 w-3.5 text-[var(--brand)]" />
                  40 vagas
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div
              data-testid="insights-dashboard-mockup"
              className="grain relative overflow-hidden rounded-[28px] bg-navy p-6 text-white shadow-[0_40px_80px_-30px_rgba(16,42,67,0.6)] sm:p-8"
            >
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="font-display text-sm font-bold text-white">CliniAtiva Insights</p>
                  <p className="text-xs text-sage/60">últimos 7 dias · tempo real</p>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-turquoise/15 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-turquoise">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-turquoise" /> live
                </span>
              </div>

              <div className="relative z-10 mt-6 grid grid-cols-3 gap-3">
                {[
                  { icon: TrendingUp, v: "98%", l: "taxa de resposta", t: "insights-stat-resposta" },
                  { icon: Clock3, v: "18–20h", l: "horário de pico", t: "insights-stat-pico" },
                  { icon: Gauge, v: "3s", l: "resposta média", t: "insights-stat-tempo" },
                ].map((s) => (
                  <div key={s.l} data-testid={s.t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <s.icon className="h-4 w-4 text-turquoise" />
                    <p className="mt-3 font-display text-xl font-extrabold text-white sm:text-2xl">{s.v}</p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-wider text-sage/60">{s.l}</p>
                  </div>
                ))}
              </div>

              <div className="relative z-10 mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-sage/70">Conversões em agendamento</p>
                  <BarChart3 className="h-4 w-4 text-turquoise" />
                </div>
                <svg viewBox="0 0 220 60" className="mt-3 h-20 w-full" preserveAspectRatio="none" aria-hidden="true">
                  <motion.path
                    d={SPARK}
                    fill="none"
                    stroke="#27B3B0"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.8, ease: "easeInOut" }}
                  />
                </svg>
                <div className="mt-5 flex h-20 items-end gap-2">
                  {BARS.map((b, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-2">
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: `${b}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className={`w-full rounded-t-md ${i === 4 ? "bg-turquoise" : "bg-sage/25"}`}
                      />
                      <span className="text-[9px] text-sage/50">{DAYS[i]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="relative z-10 mt-4 text-center text-[10px] text-sage/40">
                * Painel ilustrativo com dados de demonstração — não representa métricas reais de clientes.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
